import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import { LoggerService } from '@sa/core/services/logger.service';
import { appInsights } from '@sa/app/app.config';
import { ApiErrorResponse } from '@sa/shared/interfaces/api-error-response';

@Injectable()
export class ErrorService implements ErrorHandler {
  private notification = inject(LoggerService);
  private router = inject(Router);

  handleError(error: Error | HttpErrorResponse): void {
    // Ignore benign ObjectUnsubscribedError thrown from RxJS internals used by
    // third-party libraries (e.g. SignalR/MSAL) during route transitions.
    if (!(error instanceof HttpErrorResponse) && this.isObjectUnsubscribedError(error)) {
      console.warn('Ignored ObjectUnsubscribedError:', error.message);
      return;
    }

    appInsights?.trackException({ exception: error });

    if (error instanceof HttpErrorResponse) {
      const parsedError = this.parseErrorObject(error.error);
      const consolidatedErrors: string[] = [];
      let showTraceId = false;

      switch (error.status) {
        case HttpStatusCode.BadRequest:
          if (parsedError.details) {
            if (
              typeof parsedError.details === 'object' &&
              !Array.isArray(parsedError.details)
            ) {
              Object.entries(parsedError.details).forEach(([key, messages]) => {
                consolidatedErrors.push(
                  `[${key}] ${Array.isArray(messages) ? messages.join(', ') : messages}`
                );
              });
            } else if (typeof parsedError.details === 'string') {
              consolidatedErrors.push(parsedError.details);
            }
          } else if (parsedError.message) {
            consolidatedErrors.push(parsedError.message);
          } else {
            consolidatedErrors.push('Bad request');
          }
          break;

        case HttpStatusCode.Unauthorized:
        case HttpStatusCode.Forbidden:
          const isDeeplinkRaw = sessionStorage.getItem('isDeeplink');
          const isDeeplink = isDeeplinkRaw === 'true';
          if (isDeeplinkRaw === null || !isDeeplink) {
            consolidatedErrors.push(
              'You are not authorized to access this resource.'
            );
            if (parsedError?.message) {
              consolidatedErrors.push(parsedError?.message);
            }
            this.router.navigate(['/no-auth']);
          }
          break;

        case HttpStatusCode.NotFound:
          if (parsedError.message) {
            consolidatedErrors.push(parsedError.message);
          } else {
            consolidatedErrors.push('Resource not found.');
          }
          break;

        default:
          if (parsedError.traceId) {
            showTraceId = true;
          }
          if (parsedError.message) {
            consolidatedErrors.push(parsedError.message);
          } else {
            consolidatedErrors.push('An unexpected error occurred.');
          }
          break;
      }

      consolidatedErrors.forEach(msg => {
        const fullMessage =
          showTraceId &&
          parsedError.traceId &&
          !msg.includes(parsedError.traceId)
            ? `${msg} (Trace ID: ${parsedError.traceId})`
            : msg;
        this.notification.logApiError(fullMessage);
      });

      if (
        error.status !== HttpStatusCode.Unauthorized &&
        error.status !== HttpStatusCode.Forbidden &&
        consolidatedErrors.length === 0
      ) {
        this.router.navigate(['/error']);
      }
    } else {
      // Handle chunk load failures after deployment (stale cached index.html references old chunks)
      if (this.isChunkLoadError(error)) {
        const lastReload = sessionStorage.getItem('chunk-reload-timestamp');
        const now = Date.now();
        // Prevent infinite reload loops — only reload once per 30 seconds
        if (!lastReload || now - parseInt(lastReload, 10) > 30000) {
          sessionStorage.setItem('chunk-reload-timestamp', now.toString());
          window.location.reload();
          return;
        }
      }

      this.notification.logApiError(
        error.message ?? 'A client-side error occurred.'
      );
      console.error(error);
    }
  }

  private isChunkLoadError(error: Error): boolean {
    const message = error.message || '';
    return (
      message.includes('Failed to fetch dynamically imported module') ||
      message.includes('Loading chunk') ||
      error.name === 'ChunkLoadError'
    );
  }

  private isObjectUnsubscribedError(error: Error): boolean {
    return (
      error?.name === 'ObjectUnsubscribedError' ||
      (error?.message || '').toLowerCase().includes('object unsubscribed')
    );
  }

  private parseErrorObject(error: ApiErrorResponse | string): {
    message?: string;
    traceId?: string;
    details?: Record<string, string[]> | string;
  } {
    try {
      let err: ApiErrorResponse | any;
      if (typeof error === 'string') {
        err = JSON.parse(error);
      } else {
        err = error;
      }
      return {
        message: err?.message || err?.title,
        traceId: err?.traceId,
        details: err?.error || err?.errors,
      };
    } catch {
      return { message: 'Unknown error' };
    }
  }
}
